import { Pencil, Trash2, X } from 'lucide-react';
import React, { useState, useRef } from 'react';
import { visiteAPI, fotoAPI } from '../../services/api';
import FileMedia from '../FileMedia';

/**
 * ModuloFoto — le fotografie di una procedura.
 *
 * COS'ERA FINO ALL'11 SETTEMBRE 2026
 * I pulsanti BEFORE e AFTER leggevano il file con FileReader e lo
 * scrivevano in base64 dentro il contenuto del modulo. Tre conseguenze:
 *
 *   - le immagini non passavano dalla consegna protetta. Le altre foto e
 *     i documenti escono con un token a scadenza breve - cinque minuti,
 *     un solo file - e chi lo chiede lascia una riga nel registro
 *     accessi; queste si leggevano come parte della procedura. Sono
 *     fotografie di procedure estetiche, cioe' proprio il materiale che
 *     il modello di riservatezza considera da autorizzare;
 *   - tre megabyte di foto diventavano quattro di testo in una riga,
 *     riletti a ogni apertura della procedura e presenti in ogni backup
 *     notturno per sempre;
 *   - nessun limite di dimensione.
 *
 * COS'E' ADESSO
 * Il file si carica sulla rotta delle foto del paziente indicando la
 * procedura, e nel modulo resta SOLO il riferimento - mai il contenuto.
 * La visualizzazione passa da <FileMedia>, che chiede un token nuovo a
 * ogni apertura: il token e' a scadenza breve e non si riusa.
 *
 * PERCHE' IL RIFERIMENTO SI SALVA SUBITO
 * Il file arriva sul server nel momento in cui si sceglie; se il
 * riferimento aspettasse il pulsante «Salva», chi cambia idea e chiude
 * lascerebbe sul disco una fotografia che nessuna schermata nomina piu'.
 * Caricamento e salvataggio del riferimento sono un gesto solo.
 */

/* Il server accetta fino a 200 MB, che ha senso per un video. Per una
   fotografia di procedura no: un file cosi' grande e' quasi sempre uno
   sbaglio, e conviene dirlo subito invece di far aspettare il
   caricamento. Il messaggio nomina il file: con due o tre foto per volta
   «file troppo grande» non dice quale. */
const MAX_MB = 15;

/**
 * INCORPORATO, nel riquadro di una prestazione (23 settembre 2026). Nella
 * prova di Alex le fotografie «non c'erano»: il blocco c'era, ma per
 * caricare bisognava premere «+ Foto», che creava una galleria vuota, e poi
 * la matita della galleria - solo li' comparivano BEFORE e AFTER. In ogni
 * prestazione il riquadro delle foto adesso e' pronto: i due pulsanti si
 * vedono subito, e la galleria (il modulo) nasce al primo caricamento con
 * `creaModulo`, non prima - un riquadro aperto e basta non scrive niente in
 * cartella.
 */
const ModuloFoto = ({ modulo, visitaId, pazienteId, onUpdate, incorporato = false, creaModulo = null }) => {
    const [editing, setEditing] = useState(false);
    /* Il modulo vero, anche quando nasce qui al primo caricamento: un
       secondo caricamento prima che la procedura si ricarichi non deve
       crearne un altro. */
    const moduloVero = useRef(modulo && modulo.id ? modulo : null);
    const aperto = incorporato || editing;
    const [contenuto, setContenuto] = useState(modulo.contenuto || { foto: [], descrizione: '' });
    const [inCorso, setInCorso] = useState(null);   // 'before' | 'after'
    const [errore, setErrore] = useState(null);

    const elenco = Array.isArray(contenuto.foto) ? contenuto.foto : [];

    const handleAddFoto = (tipo) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.size > MAX_MB * 1024 * 1024) {
                setErrore(`«${file.name}» pesa ${(file.size / 1024 / 1024).toFixed(1)} MB: `
                        + `il massimo per una fotografia è ${MAX_MB} MB.`);
                return;
            }

            try {
                setErrore(null);
                setInCorso(tipo);

                const fd = new FormData();
                fd.append('file', file);
                fd.append('etichetta', tipo === 'before' ? 'Before' : 'After');
                /* E' questo campo che lega la fotografia alla procedura, e
                   che le fa seguire la riservatezza di quella procedura
                   invece di lasciarla in una galleria aperta. */
                fd.append('visita_id', String(visitaId));

                /* La galleria prima della foto: se la galleria non nasce,
                   la foto non si carica e non resta orfana sul disco. */
                if (!moduloVero.current) moduloVero.current = await creaModulo();
                const m = moduloVero.current;

                const r = await fotoAPI.upload(pazienteId, fd);
                const salvata = r.data?.data?.foto;
                if (!salvata) throw new Error('Risposta senza fotografia');

                // Nel modulo va il riferimento, non l'immagine.
                const nuovo = {
                    ...contenuto,
                    foto: [...elenco, {
                        tipo,
                        foto_id: salvata.id,
                        url: salvata.url,
                        nome: salvata.nome_file || file.name,
                        data: salvata.created_at || new Date().toISOString(),
                    }],
                };
                await visiteAPI.updateModulo(m.id, {
                    titolo: m.titolo, contenuto: nuovo, ordine: m.ordine,
                });
                setContenuto(nuovo);
                onUpdate();
            } catch (err) {
                setErrore(err?.response?.data?.message || err?.message
                          || 'Non riesco a caricare la fotografia');
            } finally {
                setInCorso(null);
            }
        };
        input.click();
    };

    /* Togliere una fotografia dal modulo la toglie anche dall'archivio:
       lasciarla nella galleria del paziente dopo averla rimossa dalla
       procedura vorrebbe dire che sparisce da dove ha un significato
       clinico e resta dove non ne ha. */
    const togliFoto = async (i) => {
        const f = elenco[i];
        if (!window.confirm('Togliere questa fotografia dalla procedura?')) return;
        try {
            setErrore(null);
            if (f.foto_id) await fotoAPI.remove(f.foto_id);
            const nuovo = { ...contenuto, foto: elenco.filter((_, k) => k !== i) };
            const m = moduloVero.current || modulo;
            await visiteAPI.updateModulo(m.id, {
                titolo: m.titolo, contenuto: nuovo, ordine: m.ordine,
            });
            setContenuto(nuovo);
            onUpdate();
        } catch (err) {
            setErrore(err?.response?.data?.message || 'Non riesco a togliere la fotografia');
        }
    };

    const handleSave = async () => {
        try {
            await visiteAPI.updateModulo(modulo.id, {
                titolo: modulo.titolo, contenuto, ordine: modulo.ordine,
            });
            setEditing(false);
            onUpdate();
        } catch (error) { setErrore('Errore nel salvataggio'); }
    };

    const handleDelete = async () => {
        if (!window.confirm('Eliminare questa galleria?')) return;
        try {
            await visiteAPI.deleteModulo(modulo.id);
            onUpdate();
        } catch (error) { setErrore('Errore eliminazione'); }
    };

    return (
        <div className="modulo-card modulo-foto">
            {!incorporato && (
            <div className="modulo-header">
                <h3>{modulo.titolo}</h3>
                <div className="modulo-actions">
                    {editing ? (
                        <>
                            <button onClick={handleSave} className="btn-save">Salva</button>
                            <button onClick={() => setEditing(false)} className="btn-cancel">✕</button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setEditing(true)} className="btn-edit-small">
                                <Pencil size={15} strokeWidth={1.8} aria-hidden="true" />
                            </button>
                            <button onClick={handleDelete} className="btn-delete-small">
                                <Trash2 size={15} strokeWidth={1.8} aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>
            </div>
            )}

            <div className="modulo-content">
                {aperto && (
                    <div className="foto-upload-buttons">
                        <button onClick={() => handleAddFoto('before')} className="btn-upload"
                                disabled={!!inCorso}>
                            {inCorso === 'before' ? 'Carico…' : (incorporato ? 'Carica BEFORE' : 'BEFORE')}
                        </button>
                        <button onClick={() => handleAddFoto('after')} className="btn-upload"
                                disabled={!!inCorso}>
                            {inCorso === 'after' ? 'Carico…' : (incorporato ? 'Carica AFTER' : 'AFTER')}
                        </button>
                    </div>
                )}

                {errore && <p className="modulo-errore">{errore}</p>}

                <div className="foto-gallery">
                    {elenco.length > 0 ? elenco.map((foto, i) => (
                        <div key={foto.foto_id || i} className="foto-item">
                            <div className="foto-tipo-badge">{(foto.tipo || '').toUpperCase()}</div>
                            {aperto && (
                                <button className="foto-togli" onClick={() => togliFoto(i)}
                                        title="Togli questa fotografia">
                                    <X size={13} aria-hidden="true" />
                                </button>
                            )}
                            {/* Le vecchie gallerie possono contenere immagini
                                scritte in base64 dentro il modulo: si mostrano
                                come stanno, senza token. In archivio non ce
                                n'e' nessuna, ma una riga che va in errore su
                                un dato vecchio e' peggio di due righe qui. */}
                            {String(foto.url || '').startsWith('data:')
                                ? <img src={foto.url} alt={foto.nome} />
                                : <FileMedia url={foto.url} alt={foto.nome} />}
                            <small>{new Date(foto.data).toLocaleDateString('it-IT')}</small>
                        </div>
                    )) : <p className="no-data">Nessuna foto</p>}
                </div>
            </div>
        </div>
    );
};

export default ModuloFoto;
